/**
 * Send-time inventory block for marketing campaigns. No VIN — public listings
 * show stock number only.
 */

export const CAMPAIGN_FEATURED_CARD_MAX = 3;
export const MARKETING_VEHICLE_PUBLIC_BASE = "https://optictruckworks.com/buy";

/** Email layout for selected trucks. Missing means cards for 1–3 and a list for 4+. */
export type CampaignFeaturedVehicleLayout = "cards" | "list";

export function parseCampaignFeaturedVehicleLayout(
  value: unknown,
): CampaignFeaturedVehicleLayout | undefined {
  return value === "cards" || value === "list" ? value : undefined;
}

export function defaultCampaignFeaturedVehicleLayout(
  count: number,
): CampaignFeaturedVehicleLayout {
  return count <= CAMPAIGN_FEATURED_CARD_MAX ? "cards" : "list";
}

export function resolveCampaignFeaturedVehicleLayout(
  count: number,
  layout: unknown,
): CampaignFeaturedVehicleLayout {
  return parseCampaignFeaturedVehicleLayout(layout) ?? defaultCampaignFeaturedVehicleLayout(count);
}

export interface CampaignFeaturedVehicle {
  id: string;
  year: number | null;
  make: string | null;
  model: string | null;
  size: string;
  stockNumber: string;
  miles: number | null;
  price: number | null;
  showOnWebsite: boolean;
  /** Public image URL for highlight cards. Empty when the truck has no photo. */
  photoUrl?: string | null;
}

const escapeHtml = (value: string): string =>
  value
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");

export function campaignFeaturedVehiclePublicUrl(vehicle: CampaignFeaturedVehicle): string | null {
  const id = vehicle.id.trim();
  if (!id || !vehicle.showOnWebsite) return null;
  return `${MARKETING_VEHICLE_PUBLIC_BASE}/${encodeURIComponent(id)}`;
}

export function formatCampaignVehicleTitle(vehicle: CampaignFeaturedVehicle): string {
  const parts = [vehicle.year, vehicle.make, vehicle.model]
    .map((part) => (part == null ? "" : String(part).trim()))
    .filter(Boolean);
  return parts.join(" ") || "Truck";
}

export function formatCampaignVehicleMiles(miles: number | null): string {
  if (miles == null || !Number.isFinite(miles)) return "";
  return `${miles.toLocaleString("en-US", { maximumFractionDigits: 0 })} mi`;
}

export function formatCampaignVehiclePrice(price: number | null): string {
  if (price == null || !Number.isFinite(price)) return "";
  return price.toLocaleString("en-US", { style: "currency", currency: "USD" });
}

const PHOTO_SLOT_ORDER = [
  "main",
  "front",
  "back",
  "left",
  "right",
  "interior",
  "engine",
  "trunk",
  "other",
] as const;

const safePhotoFileName = (value: unknown): string => {
  if (typeof value !== "string") return "";
  const name = value.trim();
  if (!name || name.includes("/") || name.includes("\\") || name.includes("..")) return "";
  return name;
};

/** https only. Email clients will not load a portal-auth path. */
export function campaignFeaturedVehiclePhotoUrl(value: unknown): string | null {
  if (typeof value !== "string") return null;
  const url = value.trim();
  if (!url.startsWith("https://")) return null;
  try {
    const parsed = new URL(url);
    if (parsed.protocol !== "https:") return null;
    return parsed.toString();
  } catch {
    return null;
  }
}

/** Named photo files, main first. Empty when the vehicle record has none. */
export function campaignFeaturedVehiclePhotoFileNames(data: Record<string, unknown>): string[] {
  const photos = data.photos;
  if (!photos || typeof photos !== "object") return [];
  const record = photos as Record<string, unknown>;
  const names: string[] = [];
  const seen = new Set<string>();
  for (const key of PHOTO_SLOT_ORDER) {
    const name = safePhotoFileName(record[key]);
    if (!name || seen.has(name)) continue;
    seen.add(name);
    names.push(name);
  }
  return names;
}

/** Storage folder for this truck's photos. Falls back to master_inventory/{id}. */
export function campaignFeaturedVehiclePhotoFolder(
  id: string,
  data: Record<string, unknown>,
): string {
  const raw = typeof data.photo_storage_path === "string" ? data.photo_storage_path.trim() : "";
  const folder = raw.replace(/^\/+|\/+$/g, "");
  if (folder && !folder.includes("..") && !folder.includes("\\")) return folder;
  return `master_inventory/${id.trim()}`;
}

export function campaignFeaturedVehicleFromRecord(
  id: string,
  data: Record<string, unknown>,
): CampaignFeaturedVehicle | null {
  const trimmed = id.trim();
  if (!trimmed) return null;
  const yearRaw = data.year;
  const year =
    typeof yearRaw === "number" && Number.isFinite(yearRaw)
      ? yearRaw
      : typeof yearRaw === "string" && yearRaw.trim() && Number.isFinite(Number(yearRaw))
        ? Number(yearRaw)
        : null;
  const milesRaw = data.miles;
  const miles =
    typeof milesRaw === "number" && Number.isFinite(milesRaw) ? milesRaw : null;
  const priceRaw = data.optic_list_price;
  const price =
    typeof priceRaw === "number" && Number.isFinite(priceRaw) ? priceRaw : null;
  return {
    id: trimmed,
    year,
    make: typeof data.make === "string" ? data.make : null,
    model: typeof data.model === "string" ? data.model : null,
    size: typeof data.size === "string" ? data.size.trim() : "",
    stockNumber: typeof data.stock_number === "string" ? data.stock_number.trim() : "",
    miles,
    price,
    showOnWebsite: data.show_on_website === true,
    photoUrl: campaignFeaturedVehiclePhotoUrl(data.photo_url),
  };
}

const detailLine = (vehicle: CampaignFeaturedVehicle): string => {
  const bits = [
    vehicle.size ? `Size ${vehicle.size}` : "",
    vehicle.stockNumber ? `Stock ${vehicle.stockNumber}` : "",
    formatCampaignVehicleMiles(vehicle.miles),
    formatCampaignVehiclePrice(vehicle.price),
  ].filter(Boolean);
  return bits.join(" · ");
};

const CARD_COLUMNS = 3;

const cardHtml = (vehicle: CampaignFeaturedVehicle): string => {
  const title = escapeHtml(formatCampaignVehicleTitle(vehicle));
  const url = campaignFeaturedVehiclePublicUrl(vehicle);
  const link = url
    ? `<a href="${escapeHtml(url)}" style="color:#1976d2;text-decoration:underline;font-size:12px;">View truck</a>`
    : "";
  const photo = campaignFeaturedVehiclePhotoUrl(vehicle.photoUrl);
  const image = photo
    ? `<img src="${escapeHtml(photo)}" alt="${title}" width="160" height="110" style="display:block;width:160px;max-width:100%;height:110px;object-fit:cover;border:0;margin:0 0 8px;border-radius:4px;" />`
    : "";
  const details = [
    vehicle.size ? `Size ${escapeHtml(vehicle.size)}` : "",
    vehicle.stockNumber ? `Stock ${escapeHtml(vehicle.stockNumber)}` : "",
    escapeHtml(formatCampaignVehicleMiles(vehicle.miles)),
    escapeHtml(formatCampaignVehiclePrice(vehicle.price)),
  ].filter(Boolean);
  return `<table role="presentation" width="100%" cellpadding="0" cellspacing="0" style="border:1px solid #e0e0e0;border-radius:8px;">
  <tr><td style="padding:10px;font-family:Arial,sans-serif;">
    ${image}
    <div style="font-size:13px;font-weight:600;line-height:1.3;color:#222222;">${title}</div>
    ${details.map((line) => `<div style="margin-top:2px;font-size:12px;line-height:1.3;color:#555555;">${line}</div>`).join("")}
    ${link ? `<div style="margin-top:6px;">${link}</div>` : ""}
  </td></tr>
</table>`;
};

/** Three cards per row. Empty cells keep the last row from stretching. */
const cardsHtml = (vehicles: CampaignFeaturedVehicle[]): string => {
  const rows: string[] = [];
  for (let i = 0; i < vehicles.length; i += CARD_COLUMNS) {
    const slice = vehicles.slice(i, i + CARD_COLUMNS);
    const cells = slice
      .map(
        (vehicle) =>
          `<td width="33%" valign="top" style="width:33%;padding:0 6px 12px 0;">${cardHtml(vehicle)}</td>`,
      )
      .join("");
    const pads = Array.from({ length: CARD_COLUMNS - slice.length }, () => (
      `<td width="33%" style="width:33%;padding:0 6px 12px 0;">&nbsp;</td>`
    )).join("");
    rows.push(`<tr>${cells}${pads}</tr>`);
  }
  return `<table role="presentation" width="100%" cellpadding="0" cellspacing="0" style="border-collapse:collapse;">${rows.join("")}</table>`;
};

const tableHtml = (vehicles: CampaignFeaturedVehicle[]): string => {
  const rows = vehicles
    .map((vehicle) => {
      const title = escapeHtml(formatCampaignVehicleTitle(vehicle));
      const stock = escapeHtml(vehicle.stockNumber || "—");
      const size = escapeHtml(vehicle.size || "—");
      const miles = escapeHtml(formatCampaignVehicleMiles(vehicle.miles) || "—");
      const price = escapeHtml(formatCampaignVehiclePrice(vehicle.price) || "—");
      const url = campaignFeaturedVehiclePublicUrl(vehicle);
      const link = url
        ? `<a href="${escapeHtml(url)}" style="color:#1976d2;">View</a>`
        : "";
      return `<tr>
        <td style="padding:8px;border-top:1px solid #e0e0e0;">${stock}</td>
        <td style="padding:8px;border-top:1px solid #e0e0e0;">${title}</td>
        <td style="padding:8px;border-top:1px solid #e0e0e0;">${size}</td>
        <td style="padding:8px;border-top:1px solid #e0e0e0;">${miles}</td>
        <td style="padding:8px;border-top:1px solid #e0e0e0;">${price}</td>
        <td style="padding:8px;border-top:1px solid #e0e0e0;">${link}</td>
      </tr>`;
    })
    .join("");
  return `<table role="presentation" width="100%" cellpadding="0" cellspacing="0" style="border-collapse:collapse;font-family:Arial,sans-serif;font-size:14px;color:#333333;">
  <tr>
    <th align="left" style="padding:8px;">Stock</th>
    <th align="left" style="padding:8px;">Truck</th>
    <th align="left" style="padding:8px;">Size</th>
    <th align="left" style="padding:8px;">Miles</th>
    <th align="left" style="padding:8px;">Price</th>
    <th align="left" style="padding:8px;"></th>
  </tr>
  ${rows}
</table>`;
};

export function orderedCampaignFeaturedVehicles(
  ids: readonly string[],
  recordsById: ReadonlyMap<string, Record<string, unknown>>,
): CampaignFeaturedVehicle[] {
  const out: CampaignFeaturedVehicle[] = [];
  const seen = new Set<string>();
  for (const raw of ids) {
    const id = String(raw ?? "").trim();
    if (!id || seen.has(id)) continue;
    seen.add(id);
    const data = recordsById.get(id);
    if (!data) continue;
    const vehicle = campaignFeaturedVehicleFromRecord(id, data);
    if (vehicle) out.push(vehicle);
  }
  return out;
}

/** Cards or a compact table. Missing layout follows the 1–3 / 4+ default. Empty input is empty. */
export function campaignFeaturedVehiclesHtml(
  vehicles: CampaignFeaturedVehicle[],
  layout?: unknown,
): string {
  const list = vehicles.filter((vehicle) => vehicle.id.trim());
  if (list.length === 0) return "";
  const resolved = resolveCampaignFeaturedVehicleLayout(list.length, layout);
  const body = resolved === "cards" ? cardsHtml(list) : tableHtml(list);
  return `<div style="margin-top:24px;">${body}</div>`;
}
